// ZIP imports retain the export's storage strings and historical merge semantics.
const legacyKeys = {
  AlltheCSS: 'CSS_LG', AlltheCSSBP1: 'CSS_MD', AlltheCSSBP2: 'CSS_SM',
  AlltheCSSBP3: 'CSS_XS', AlltheHTML: 'HTML_index'
};

function snapshot(storage) {
  const entries = [];
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    entries.push([key, storage.getItem(key)]);
  }
  return entries;
}

function restore(storage, entries) {
  const previous = new Map(entries);
  const failures = [];
  // Free space occupied by changed/imported values without clearing untouched data.
  for (const [key, value] of snapshot(storage)) {
    if (previous.get(key) !== value) {
      try { storage.removeItem(key); } catch (error) { failures.push(error); }
    }
  }
  for (const [key, value] of entries) {
    try {
      if (storage.getItem(key) !== value) storage.setItem(key, value);
    } catch (error) { failures.push(error); }
  }
  if (failures.length) throw failures[0];
}

export async function importProjectZip(file, {
  JSZip, imageStorage, localStorage, sessionStorage, makeImage, loadProject,
  restoreEditor, prepareProject = () => {}
}) {
  let zip;
  try {
    zip = await new JSZip().loadAsync(file, { checkCRC32: true });
  } catch (cause) {
    const error = new Error('Unable to import project. The ZIP file is invalid or corrupted. Your current project has not been changed.');
    error.code = 'INVALID_PROJECT_ZIP';
    error.cause = cause;
    throw error;
  }
  const projectFile = zip.file('project.json');
  if (!projectFile) throw new Error('The ZIP is missing project.json.');
  let project;
  try {
    project = JSON.parse(await projectFile.async('string'));
  } catch (error) {
    throw new Error('Unable to read project.json: ' + error.message);
  }
  if (!project || typeof project !== 'object' || Array.isArray(project)) {
    throw new Error('project.json must contain a project storage object.');
  }
  const incoming = new Map();
  for (const key of Object.keys(project)) {
    if (key === 'domposer_version') continue;
    if (typeof project[key] !== 'string') {
      throw new Error('Invalid project storage value: ' + key);
    }
    incoming.set(Object.prototype.hasOwnProperty.call(legacyKeys, key) ? legacyKeys[key] : key, project[key]);
  }
  if (![...incoming.keys()].some(key => key.startsWith('HTML_') || ['CSS_LG', 'CSS_MD', 'CSS_SM', 'CSS_XS'].includes(key))) {
    throw new Error('project.json does not contain project HTML or CSS.');
  }
  // Validate the effective merged state, including legacy and optional defaults.
  const effective = new Map(snapshot(localStorage));
  for (const [key, value] of incoming) effective.set(key, value);
  for (const key of ['CSS_LG', 'CSS_MD', 'CSS_SM', 'CSS_XS']) {
    const value = JSON.parse(effective.get(key) || '{}');
    if (!value || typeof value !== 'object' || Array.isArray(value) ||
        Object.values(value).some(rule => !rule || typeof rule !== 'object' || Array.isArray(rule))) {
      throw new Error('Invalid breakpoint CSS: ' + key);
    }
  }
  if (effective.has('mobileFirst') && typeof JSON.parse(effective.get('mobileFirst')) !== 'boolean') {
    throw new Error('Invalid mobileFirst setting.');
  }
  const pages = JSON.parse(effective.get('page_names') || '["index"]');
  if (!Array.isArray(pages) || !pages.length || pages.some(page => typeof page !== 'string' || !page)) {
    throw new Error('Invalid project page_names.');
  }
  for (const key of ['breakPoint1Value', 'breakPoint2Value', 'breakPoint3Value']) {
    if (effective.has(key) && !Number.isFinite(JSON.parse(effective.get(key)))) {
      throw new Error('Invalid breakpoint value: ' + key);
    }
  }
  await prepareProject(effective);
  // Finish all extraction before touching any existing data. Directories are not images.
  const images = [];
  for (const entry of Object.values(zip.files)) {
    if (entry.dir || !entry.name.startsWith('images/')) continue;
    const key = entry.name.slice(7);
    const extension = key.split('.').pop().toLowerCase();
    const types = { svg: 'image/svg+xml', png: 'image/png', jpeg: 'image/jpeg',
      jpg: 'image/jpeg', gif: 'image/gif' };
    const type = Object.prototype.hasOwnProperty.call(types, extension) ? types[extension] : '';
    images.push([key, makeImage(await entry.async('blob'), type)]);
  }
  const backup = {
    local: snapshot(localStorage), session: snapshot(sessionStorage), images: []
  };
  await imageStorage.iterate((value, key) => { backup.images.push([key, value]); });
  const touchedImages = new Set();
  try {
    // Sequential writes mean rollback cannot race a still-running failed batch.
    for (const [key, value] of images) {
      touchedImages.add(key);
      await imageStorage.setItem(key, value);
    }
    const incomingImageKeys = new Set(images.map(([key]) => key));
    for (const [key] of backup.images) {
      if (!incomingImageKeys.has(key)) {
        touchedImages.add(key);
        await imageStorage.removeItem(key);
      }
    }
    for (const [key, value] of incoming) localStorage.setItem(key, value);
    sessionStorage.clear();
    await loadProject();
  } catch (cause) {
    const failures = [];
    for (const operation of [
      async () => {
        const errors = [];
        const previousKeys = new Set(backup.images.map(([key]) => key));
        for (const [key] of images) {
          if (touchedImages.has(key) && !previousKeys.has(key)) {
            try { await imageStorage.removeItem(key); } catch (error) { errors.push(error); }
          }
        }
        for (const [key, value] of backup.images) {
          if (!touchedImages.has(key)) continue;
          try { await imageStorage.setItem(key, value); } catch (error) { errors.push(error); }
        }
        if (errors.length) throw errors[0];
      },
      () => restore(localStorage, backup.local),
      () => restore(sessionStorage, backup.session),
      () => restoreEditor()
    ]) {
      try { await operation(); } catch (error) { failures.push(error); }
    }
    if (failures.length) {
      const error = new Error('Import failed and storage recovery was incomplete. Keep this tab open and download the recovery ZIP.');
      error.cause = cause;
      error.backup = backup;
      error.rollbackErrors = failures;
      throw error;
    }
    const error = new Error('Import failed. Your previous project was restored. ' + cause.message);
    error.cause = cause;
    throw error;
  }
}
